/**
 * วิเคราะห์แอด (Ads) — ดึงผลโฆษณา Meta สด (spend/ROAS/ผลลัพธ์)
 * แยกแบรนด์ (Cherlyn / Cher Scrub) + การ์ดเทียบ + ตารางจัดกลุ่ม + แจ้งเตือนอัตโนมัติ
 * ใช้ FB_ADS_TOKEN (system user) ฝั่ง worker · cache 1 ชม. · ปุ่มรีเฟรชดึงใหม่
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AdsApi, type AdsCampaign } from '@/lib/admin-api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

const RANGES = [
  { value: 'last_7d', label: '7 วัน' },
  { value: 'last_30d', label: '30 วัน' },
  { value: 'last_90d', label: '90 วัน' },
];
const baht = (n: number) => '฿' + Math.round(n).toLocaleString();
const roasColor = (r: number) => (r >= 2.5 ? 'text-green-700 border-green-300' : r < 1.5 ? 'text-red-600 border-red-300' : 'text-amber-700 border-amber-300');

// แยกแบรนด์จากชื่อแคมเปญ (ชื่อมี scrub/สครับ = Cher Scrub, ที่เหลือ = Cherlyn/โบว์)
type BrandKey = 'scrub' | 'cherlyn';
const brandOf = (name: string): BrandKey => (/scrub|สครับ/i.test(name) ? 'scrub' : 'cherlyn');
const BRAND_META: Record<BrandKey, { label: string; emoji: string; tint: string }> = {
  scrub: { label: 'Cher Scrub', emoji: '🩺', tint: 'border-l-teal-500' },
  cherlyn: { label: 'Cherlyn (โบว์)', emoji: '🎀', tint: 'border-l-pink-500' },
};

type BrandAgg = { spend: number; revenue: number; purchases: number; orders: number; campaigns: AdsCampaign[] };
function aggregate(campaigns: AdsCampaign[]): Record<BrandKey, BrandAgg> {
  const init = (): BrandAgg => ({ spend: 0, revenue: 0, purchases: 0, orders: 0, campaigns: [] });
  const out: Record<BrandKey, BrandAgg> = { scrub: init(), cherlyn: init() };
  for (const c of campaigns) {
    const a = out[brandOf(c.name)];
    a.spend += c.spend; a.revenue += c.revenue; a.purchases += c.purchases; a.orders += c.orders;
    a.campaigns.push(c);
  }
  for (const k of Object.keys(out) as BrandKey[]) out[k].campaigns.sort((x, y) => y.spend - x.spend);
  return out;
}

function Stat({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="border rounded-lg p-3 bg-card">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-lg font-bold">{value}</div>
      {sub && <div className="text-[11px] text-muted-foreground">{sub}</div>}
    </div>
  );
}

function BrandCard({ k, a, totalSpend }: { k: BrandKey; a: BrandAgg; totalSpend: number }) {
  const m = BRAND_META[k];
  const roas = a.spend ? a.revenue / a.spend : 0;
  const aov = a.purchases ? a.revenue / a.purchases : 0;
  const cpa = a.purchases ? a.spend / a.purchases : 0;
  const share = totalSpend ? Math.round((a.spend / totalSpend) * 100) : 0;
  return (
    <div className={`border border-l-4 ${m.tint} rounded-lg p-3 bg-card`}>
      <div className="flex items-center justify-between">
        <div className="font-semibold">{m.emoji} {m.label}</div>
        <Badge variant="outline" className={`text-xs ${roasColor(roas)}`}>ROAS {roas.toFixed(2)}x</Badge>
      </div>
      <div className="mt-2 grid grid-cols-2 gap-y-1 text-sm">
        <span className="text-muted-foreground">งบยิง</span><span className="text-right font-medium">{baht(a.spend)} <span className="text-[11px] text-muted-foreground">({share}%)</span></span>
        <span className="text-muted-foreground">รายได้</span><span className="text-right font-medium">{baht(a.revenue)}</span>
        <span className="text-muted-foreground">ซื้อ</span><span className="text-right">{a.purchases.toLocaleString()}</span>
        <span className="text-muted-foreground">฿/ออเดอร์ (AOV)</span><span className="text-right">{aov ? baht(aov) : '—'}</span>
        <span className="text-muted-foreground">ทุน/ซื้อ (CPA)</span><span className="text-right">{cpa ? baht(cpa) : '—'}</span>
      </div>
    </div>
  );
}

export default function AdsPage() {
  const [range, setRange] = useState('last_30d');
  const [refreshing, setRefreshing] = useState(false);
  const q = useQuery({ queryKey: ['ads-insights', range], queryFn: () => AdsApi.insights(range) });
  const d = q.data;

  async function refresh() {
    setRefreshing(true);
    await AdsApi.insights(range, true).catch(() => {});
    await q.refetch();
    setRefreshing(false);
  }

  const campaigns = d?.campaigns ?? [];
  const byBrand = aggregate(campaigns);
  const totalSpend = byBrand.scrub.spend + byBrand.cherlyn.spend;
  // เรียงแบรนด์ตามงบมาก→น้อย
  const brandOrder = (Object.keys(byBrand) as BrandKey[]).sort((a, b) => byBrand[b].spend - byBrand[a].spend);

  // แจ้งเตือนอัตโนมัติ
  const alerts: string[] = [];
  for (const c of campaigns) {
    if (c.spend >= 3000 && c.roas < 1.5) alerts.push(`🔴 "${c.name}" ROAS ${c.roas}x ต่ำ (งบ ${baht(c.spend)}) — ควรหยุด/รื้อ`);
    else if (c.frequency >= 4.5 && c.spend >= 3000) alerts.push(`⚠️ "${c.name}" freq ${c.frequency} สูง (คนเห็นซ้ำ) — เติมครีเอทีฟใหม่`);
  }
  for (const c of campaigns) if (c.roas >= 3 && c.frequency < 4) { alerts.push(`🟢 "${c.name}" ROAS ${c.roas}x + freq ต่ำ — เพิ่มงบได้`); break; }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-lg font-semibold">📈 วิเคราะห์แอด (Meta Ads)</h2>
        <div className="flex items-center gap-2">
          <select className="border rounded-md px-2 py-1.5 text-sm" value={range} onChange={(e) => setRange(e.target.value)}>
            {RANGES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
          <Button size="sm" variant="outline" onClick={refresh} disabled={refreshing}>{refreshing ? 'กำลังดึง…' : '🔄 รีเฟรช'}</Button>
        </div>
      </div>

      {q.isLoading && <div className="text-sm text-muted-foreground py-8 text-center">กำลังโหลดข้อมูลโฆษณา…</div>}
      {d?.error && (
        <div className="text-sm bg-red-50 border border-red-200 rounded-lg p-3 text-red-700">
          ดึงข้อมูลไม่ได้: {d.error}{d.hint ? ` — ${d.hint}` : ''}{d.detail ? ` (${d.detail})` : ''}
        </div>
      )}

      {d && !d.error && d.overview && (
        <>
          {/* ภาพรวมทั้งบัญชี */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <Stat label="ใช้จ่ายรวม" value={baht(d.overview.spend)} />
            <Stat label="รายได้รวม" value={baht(d.overview.revenue)} />
            <Stat label="ROAS รวม" value={`${d.overview.roas}x`} sub="รายได้ ÷ งบ" />
            <Stat label="ซื้อ / ออเดอร์แชท" value={`${d.overview.purchases} / ${d.overview.orders}`} />
            <Stat label="Reach" value={d.overview.reach.toLocaleString()} sub={`เห็นซ้ำ ${d.overview.frequency} ครั้ง`} />
            <Stat label="CTR" value={`${d.overview.ctr}%`} sub={`CPC ${baht(d.overview.cpc)}`} />
            <Stat label="Leads" value={String(d.overview.leads)} />
            <Stat label="อัปเดต" value={d.cached ? 'cache' : 'สด'} sub={new Date(d.fetchedAt).toLocaleString('th-TH')} />
          </div>

          {/* เทียบ 2 แบรนด์ */}
          <div>
            <div className="text-sm font-medium mb-2">⚖️ เทียบรายแบรนด์</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {brandOrder.map((k) => <BrandCard key={k} k={k} a={byBrand[k]} totalSpend={totalSpend} />)}
            </div>
          </div>

          {alerts.length > 0 && (
            <div className="border rounded-lg p-3 bg-blue-50/40 space-y-1">
              <div className="text-sm font-medium">🔔 ข้อเสนอแนะอัตโนมัติ</div>
              {alerts.map((a, i) => <div key={i} className="text-xs">{a}</div>)}
            </div>
          )}

          {/* ตารางแคมเปญ จัดกลุ่มตามแบรนด์ */}
          <div className="border rounded-lg overflow-x-auto">
            <table className="w-full text-sm min-w-[680px]">
              <thead className="bg-muted/50 text-left text-xs">
                <tr>
                  <th className="px-3 py-2">แคมเปญ</th>
                  <th className="px-2 py-2 text-right">งบ</th>
                  <th className="px-2 py-2 text-right">รายได้</th>
                  <th className="px-2 py-2 text-center">ROAS</th>
                  <th className="px-2 py-2 text-right">ทุน/ซื้อ</th>
                  <th className="px-2 py-2 text-right">ซื้อ</th>
                  <th className="px-2 py-2 text-right">freq</th>
                  <th className="px-2 py-2 text-right">CTR</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {brandOrder.flatMap((k) => {
                  const a = byBrand[k];
                  if (!a.campaigns.length) return [];
                  const m = BRAND_META[k];
                  const roas = a.spend ? a.revenue / a.spend : 0;
                  return [
                    <tr key={`h-${k}`} className="bg-muted/30 text-xs font-medium">
                      <td className="px-3 py-1.5">{m.emoji} {m.label} <span className="text-muted-foreground">({a.campaigns.length})</span></td>
                      <td className="px-2 py-1.5 text-right">{baht(a.spend)}</td>
                      <td className="px-2 py-1.5 text-right">{baht(a.revenue)}</td>
                      <td className="px-2 py-1.5 text-center"><Badge variant="outline" className={`text-[11px] ${roasColor(roas)}`}>{roas.toFixed(2)}x</Badge></td>
                      <td colSpan={3} className="px-2 py-1.5 text-right text-muted-foreground">ซื้อรวม {a.purchases}</td>
                      <td className="px-2 py-1.5"></td>
                    </tr>,
                    ...a.campaigns.map((c, i) => (
                      <tr key={`${k}-${i}`}>
                        <td className="px-3 py-2 pl-6">{c.name}</td>
                        <td className="px-2 py-2 text-right">{baht(c.spend)}</td>
                        <td className="px-2 py-2 text-right">{baht(c.revenue)}</td>
                        <td className="px-2 py-2 text-center"><Badge variant="outline" className={`text-[11px] ${roasColor(c.roas)}`}>{c.roas}x</Badge></td>
                        <td className="px-2 py-2 text-right">{c.purchases ? baht(c.spend / c.purchases) : '—'}</td>
                        <td className="px-2 py-2 text-right">{c.purchases}</td>
                        <td className={`px-2 py-2 text-right ${c.frequency >= 4.5 ? 'text-amber-600 font-medium' : ''}`}>{c.frequency}</td>
                        <td className="px-2 py-2 text-right">{c.ctr}%</td>
                      </tr>
                    )),
                  ];
                })}
              </tbody>
            </table>
          </div>
          <p className="text-[11px] text-muted-foreground">บัญชีแอด act_{d.account} (รันทั้งโบว์+Scrub) · แยกแบรนด์จากชื่อแคมเปญ · ROAS เขียว ≥2.5 / แดง &lt;1.5 · ผ่าน FB system user token</p>
        </>
      )}
    </div>
  );
}
